import React, {useCallback, useEffect, useState} from 'react';
import {
  ActivityIndicator,
  Image,
  Platform,
  RefreshControl,
  ScrollView,
  View,
} from 'react-native';

import Avatar from '/component/base/Avatar';
import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import ModalHeader from '/component/layout/ModalHeader';
import {ManageMySiteViewTestIDs} from '/constant/TestIDs';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';
import {openURL} from '/util/linking.util';

import {
  getCountryLabel,
  normalizeCountry,
  sortSocialLinks,
} from '../ManageMySite.constants';
import {ManageMySiteViewProps} from '../ManageMySite.types';
import {StaticFocusable} from '../sections/StaticFocusable';
import {formatDisplayPhone} from '../util/manageMySite.util';
import {FieldGroup} from '../widgets/FieldGroup';
import {SectionTitle} from '../widgets/SectionTitle';

import {styles} from './ManageMySiteViewScreen.style';

export const SectionLoader = ({label}: {label: string}) => (
  <StaticFocusable label={label} style={styles.loaderContainer}>
    <ActivityIndicator
      size="small"
      accessibilityRole="progressbar"
      accessibilityLabel={label}
      accessible={false}
    />
  </StaticFocusable>
);

export const ManageMySiteViewScreenComponent = ({
  onClose,
  onEdit,
  onEditSocialLinks,
  avatarUrl = '',
  baseWebsiteUrl = '',
  webAlias = '',
  displayName = '',
  email = '',
  phonePrimary = '',
  phoneSecondary = '',
  street = '',
  city = '',
  state = '',
  country = '',
  zipcode = '',
  about = '',
  transformationBeforeUrl = '',
  transformationAfterUrl = '',
  socialLinks = [],
  isBusinessProfileLoading = false,
  isSocialMediaLoading = false,
  hasBusinessProfile = false,
  hasSocialMedia = false,
  savingSection = null,
  isSocialMediaError = false,
  getBusinessProfile = () => {},
  getSocialMedia = () => {},
}: ManageMySiteViewProps) => {
  const fullUrl = `${baseWebsiteUrl?.replace(/\/$/, '')}/${webAlias}`;
  const addressSummary = [street, city, state, zipcode]
    .filter(Boolean)
    .join(', ');

  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowReadMore, setShouldShowReadMore] = useState(false);
  const [cacheKey] = useState(() => Date.now());
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (isBusinessProfileLoading || hasBusinessProfile) {
      setIsInitialLoad(false);
    }
  }, [isBusinessProfileLoading, hasBusinessProfile]);

  const handleRefresh = useCallback(async () => {
    try {
      setIsRefreshing(true);
      await getBusinessProfile?.();
      await getSocialMedia?.();
    } finally {
      setIsRefreshing(false);
    }
  }, [getBusinessProfile, getSocialMedia]);

  const addCacheBust = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('data:')) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${cacheKey}`;
  };

  const aboutText =
    about ||
    tt('aboutPlaceholder', 'Tell your story and what makes you unique...');

  useEffect(() => {
    if (Platform.OS !== 'web' || !about) return;
    const timer = setTimeout(() => {
      const container = document.querySelector(
        '[data-testid="about-me-text-container"]',
      );
      const textNode = container?.querySelector('div[dir="auto"]');
      if (textNode instanceof HTMLElement) {
        // Create a temporary clone to measure full height without the 2-line clamp
        const clone = textNode.cloneNode(true) as HTMLElement;
        clone.style.webkitLineClamp = 'none';
        // @ts-ignore
        clone.style.lineClamp = 'none';
        clone.style.position = 'absolute';
        clone.style.visibility = 'hidden';
        clone.style.height = 'auto';
        clone.style.width = `${textNode.offsetWidth}px`;
        document.body.appendChild(clone);
        const fullHeight = clone.scrollHeight;
        const styles = window.getComputedStyle(textNode);
        const lineHeight = parseFloat(styles.lineHeight) || 20;
        document.body.removeChild(clone);
        // If full height is greater than 2 lines, show read more
        setShouldShowReadMore(fullHeight > lineHeight * 2 + 2);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [about]);

  const isUS = (normalizeCountry(country) || 'US') === 'US';
  const displayPrimaryPhone = formatDisplayPhone(phonePrimary, isUS);
  const displaySecondaryPhone = formatDisplayPhone(phoneSecondary, isUS);
  const hasLinks = socialLinks?.some(link => !!link.url);

  const showInitialFullScreenLoader =
    isInitialLoad || (isBusinessProfileLoading && !hasBusinessProfile);

  const showProfileSectionLoader = savingSection === 'businessProfile';

  const showSocialLoader =
    savingSection === 'socialLinks' ||
    (isSocialMediaLoading && !hasSocialMedia);

  const showSocialError = isSocialMediaError && !hasSocialMedia;

  const defaultPhotoUrl =
    'https://qa5-backoffice.optavia.com/Content/images/avatars/default-avatar.png';

  const renderHeaderProps = {
    title: tt('manageSite', 'Manage My Site'),
    onClose,
    closeIconName: 'chevron-left',
    accessibilityLabel: 'Manage My Site',
    actionLabel: tt('edit', 'Edit'),
    accessibilityHint: 'Returns to the previous screen',
    onActionPress: onEdit,
    style: {
      base: styles.base,
      headerText: styles.headerText,
      actionButton: styles.headerEditButton,
      actionText: styles.headerEditText,
      actionIcon: styles.goBackIcon,
    },
  };

  // if (showInitialFullScreenLoader) {
  //   return (
  //     <ModalHeader {...renderHeaderProps}>
  //       <View style={styles.fullScreenLoader} accessible={false}>
  //         <ActivityIndicator
  //           size="large"
  //           accessibilityRole="progressbar"
  //           accessibilityLabel="Loading profile information"
  //         />
  //       </View>
  //     </ModalHeader>
  //   );
  // }

  return (
    <ModalHeader {...renderHeaderProps}>
      <ScrollView
        refreshControl={
          <RefreshControl refreshing={isRefreshing} onRefresh={handleRefresh} />
        }
        accessible={false}
        style={styles.scrollViewBase}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}>
        {showInitialFullScreenLoader ? (
          <View style={styles.fullScreenLoader} accessible={false}>
            <ActivityIndicator
              size="large"
              accessibilityRole="progressbar"
              accessibilityLabel="Loading profile information"
            />
          </View>
        ) : (
          <>
            <SectionTitle
              title={tt('profileImage', 'Coaching Profile Image')}
              style={styles.sectionTitleTight}
            />

            <StaticFocusable
              label={
                displayName
                  ? `Profile image of ${displayName}`
                  : 'Profile image'
              }
              testID={ManageMySiteViewTestIDs.profile.imageContainer}
              style={styles.avatarFocusContainer}>
              <Avatar
                testID={ManageMySiteViewTestIDs.profile.image}
                avatarUrl={addCacheBust(avatarUrl)}
                size={80}
                isCoach
                accessible={false}
                fullName={displayName}
              />
            </StaticFocusable>

            {showProfileSectionLoader ? (
              <View
                style={styles.profileSectionLoaderContainer}
                accessible={false}
                importantForAccessibility="no">
                <SectionLoader label="Loading profile information" />
              </View>
            ) : (
              <>
                <SectionTitle
                  title={tt('website', 'Website')}
                  accessibilityRole="header"
                />

                {webAlias ? (
                  <KeyboardFocusable
                    testID={ManageMySiteViewTestIDs.website.linkContainer}
                    onPress={() => openURL('url', fullUrl)}
                    accessible
                    accessibilityRole="link"
                    accessibilityLabel={`${
                      fullUrl || baseWebsiteUrl
                    }, Coach website`}
                    accessibilityHint="Coach website. Opens in browser"
                    style={styles.websiteLinkContainer}>
                    <Text
                      accessible={false}
                      allowFontScaling={true}
                      maxFontSizeMultiplier={2}
                      style={styles.websiteLink}
                      isBold
                      testID={ManageMySiteViewTestIDs.website.linkText}>
                      {fullUrl || baseWebsiteUrl}
                    </Text>
                  </KeyboardFocusable>
                ) : (
                  <StaticFocusable
                    label="No website URL is set up yet"
                    testID={ManageMySiteViewTestIDs.website.noUrlContainer}>
                    <Text
                      accessible={false}
                      allowFontScaling={true}
                      maxFontSizeMultiplier={2}
                      style={styles.noWebsiteUrl}
                      testID={ManageMySiteViewTestIDs.website.noUrlText}>
                      No website URL is set up yet.
                    </Text>
                  </StaticFocusable>
                )}

                <SectionTitle title={tt('contact', 'Contact Information')} />

                <FieldGroup
                  label={tt('displayName', 'Display Name')}
                  value={displayName}
                />
                <FieldGroup
                  label={tt('emailAddress', 'Email Address')}
                  value={email}
                />
                <FieldGroup
                  label={tt('phoneNumber', 'Primary Phone Number')}
                  value={displayPrimaryPhone}
                />
                {!!phoneSecondary && (
                  <FieldGroup
                    label={tt('secondaryPhone', 'Secondary Phone Number')}
                    value={displaySecondaryPhone}
                  />
                )}
                <FieldGroup
                  label={tt('displayAddress', 'Display Address')}
                  value={addressSummary}
                />
                {normalizeCountry(country) !== 'US' && (
                  <FieldGroup
                    label={tt('country', 'Country')}
                    value={getCountryLabel(country)}
                  />
                )}

                <SectionTitle
                  title={tt('about', 'About Me')}
                  style={styles.sectionTitleTight}
                />

                {!!about && (
                  <View
                    pointerEvents="none"
                    accessible={false}
                    importantForAccessibility="no-hide-descendants"
                    style={styles.hiddenOverlay}>
                    <Text
                      accessible={false}
                      allowFontScaling
                      maxFontSizeMultiplier={2}
                      style={styles.aboutMeText}
                      onTextLayout={(e: any) =>
                        setShouldShowReadMore(e.nativeEvent.lines.length > 2)
                      }>
                      {about}
                    </Text>
                  </View>
                )}

                <StaticFocusable
                  label={String(aboutText || '')}
                  testID={ManageMySiteViewTestIDs.about.container}>
                  <Text
                    allowFontScaling={true}
                    maxFontSizeMultiplier={2}
                    testID={ManageMySiteViewTestIDs.about.text}
                    accessible={false}
                    style={[
                      styles.aboutMeText,
                      !about && styles.placeholderText,
                    ]}
                    numberOfLines={isExpanded ? undefined : 2}
                    ellipsizeMode="tail">
                    {aboutText}
                  </Text>
                </StaticFocusable>

                {shouldShowReadMore && !!about && (
                  <KeyboardFocusable
                    testID={ManageMySiteViewTestIDs.about.readMoreToggle}
                    style={styles.readMoreContainer}
                    onPress={() => setIsExpanded(prev => !prev)}
                    accessible
                    accessibilityRole="button"
                    accessibilityLabel={isExpanded ? 'Read less' : 'Read more'}
                    accessibilityHint={
                      isExpanded
                        ? 'Collapses the about me section'
                        : 'Expands the about me section'
                    }>
                    <Text
                      allowFontScaling={true}
                      maxFontSizeMultiplier={2}
                      accessible={false}
                      importantForAccessibility="no"
                      style={styles.readMoreText}
                      testID={ManageMySiteViewTestIDs.about.readMoreToggleText}>
                      {isExpanded
                        ? tt('readLess', 'Read less')
                        : tt('readMore', 'Read more')}
                    </Text>
                    <View
                      testID={ManageMySiteViewTestIDs.about.readMoreIcon}
                      accessible={false}
                      importantForAccessibility="no-hide-descendants">
                      <Icon
                        name={isExpanded ? 'chevron-up' : 'chevron-down'}
                        style={styles.readMoreIcon}
                        accessible={false}
                        importantForAccessibility="no-hide-descendants"
                      />
                    </View>
                  </KeyboardFocusable>
                )}
              </>
            )}

            <SectionTitle title={tt('photos', 'Transformation Photos')} />

            <View style={styles.photoGrid} accessible={false}>
              <View style={styles.viewPhotoCard} accessible={false}>
                <StaticFocusable
                  label={String(tt('before', 'Before'))}
                  testID={`${ManageMySiteViewTestIDs.photos.beforeLabel}-container`}>
                  <Text
                    allowFontScaling={true}
                    maxFontSizeMultiplier={2}
                    testID={ManageMySiteViewTestIDs.photos.beforeLabel}
                    style={styles.fieldLabel}
                    accessible={false}>
                    {tt('before', 'Before')}
                  </Text>
                </StaticFocusable>
                <StaticFocusable
                  label="Before transformation photo"
                  role="image"
                  testID={ManageMySiteViewTestIDs.photos.beforeContainer}
                  style={styles.photoFocusContainer}>
                  <Image
                    testID={ManageMySiteViewTestIDs.photos.beforeImage}
                    source={{
                      uri:
                        addCacheBust(transformationBeforeUrl) ||
                        defaultPhotoUrl,
                    }}
                    style={styles.photoMock}
                    accessible={false}
                    accessibilityLabel="Before transformation photo"
                    importantForAccessibility="no-hide-descendants"
                  />
                </StaticFocusable>
              </View>

              <View style={styles.viewPhotoCard} accessible={false}>
                <StaticFocusable
                  label={String(tt('after', 'After'))}
                  testID={`${ManageMySiteViewTestIDs.photos.afterLabel}-container`}>
                  <Text
                    allowFontScaling={true}
                    maxFontSizeMultiplier={2}
                    testID={ManageMySiteViewTestIDs.photos.afterLabel}
                    style={styles.fieldLabel}
                    accessible={false}>
                    {tt('after', 'After')}
                  </Text>
                </StaticFocusable>
                <StaticFocusable
                  label="After transformation photo"
                  role="image"
                  testID={ManageMySiteViewTestIDs.photos.afterContainer}
                  style={styles.photoFocusContainer}>
                  <Image
                    testID={ManageMySiteViewTestIDs.photos.afterImage}
                    source={{
                      uri:
                        addCacheBust(transformationAfterUrl) || defaultPhotoUrl,
                    }}
                    style={styles.photoMock}
                    accessible={false}
                    accessibilityLabel="After transformation photo"
                    importantForAccessibility="no-hide-descendants"
                  />
                </StaticFocusable>
              </View>
            </View>

            <SectionTitle title={tt('socialMedia', 'Social Media')} />

            {showSocialLoader ? (
              <SectionLoader label="Loading social links" />
            ) : showSocialError ? (
              <KeyboardFocusable
                onPress={getSocialMedia}
                accessible
                accessibilityRole="button"
                accessibilityLabel="Unable to load social links. Tap to try again."
                accessibilityHint="Retries loading social links"
                testID={ManageMySiteViewTestIDs.social.error}>
                <View
                  accessible={false}
                  importantForAccessibility="no-hide-descendants">
                  <Text
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    accessible={false}
                    style={styles.errorText}
                    testID={ManageMySiteViewTestIDs.social.errorText}>
                    Unable to load social links. Tap to try again.
                  </Text>
                </View>
              </KeyboardFocusable>
            ) : socialLinks?.filter(link => !!link.url).length === 0 ? (
              <StaticFocusable
                label="No social links added yet"
                testID={ManageMySiteViewTestIDs.social.noLinksContainer}>
                <Text
                  allowFontScaling={true}
                  maxFontSizeMultiplier={2}
                  style={styles.socialInfoText}
                  accessible={false}
                  testID={ManageMySiteViewTestIDs.social.noLinksText}>
                  No social links added yet.
                </Text>
              </StaticFocusable>
            ) : (
              sortSocialLinks(socialLinks)
                ?.filter(link => !!link.url)
                ?.map((link, index) => {
                  const displayValue = (
                    link.resolvedUrl ||
                    link.url ||
                    ''
                  ).replace(/^https?:\/\//, '');
                  const safeType = (link.type || 'social')
                    ?.toLowerCase()
                    ?.replace(/\s+/g, '-');

                  return (
                    <View
                      key={`${link.socialNetworkID}-${index}`}
                      accessible={false}
                      style={styles.socialLinkRow}>
                      <StaticFocusable
                        label={link.type}
                        testID={`${ManageMySiteViewTestIDs.social.linkLabel(
                          safeType,
                          index,
                        )}-container`}>
                        <Text
                          allowFontScaling
                          maxFontSizeMultiplier={2}
                          testID={ManageMySiteViewTestIDs.social.linkLabel(
                            safeType,
                            index,
                          )}
                          style={styles.socialPlatformLabel}
                          accessible={false}>
                          {link.type}
                        </Text>
                      </StaticFocusable>

                      <KeyboardFocusable
                        testID={ManageMySiteViewTestIDs.social.linkButton(
                          safeType,
                          index,
                        )}
                        style={styles.socialItem}
                        onPress={() =>
                          openURL('url', link.resolvedUrl || link.url)
                        }
                        accessible
                        accessibilityRole="link"
                        accessibilityLabel={`${displayValue}, ${link.type}`}
                        accessibilityHint={`Opens ${link.type} in browser`}>
                        <Text
                          testID={ManageMySiteViewTestIDs.social.linkText(
                            safeType,
                            index,
                          )}
                          allowFontScaling
                          maxFontSizeMultiplier={2}
                          accessible={false}
                          style={styles.socialUrlText}
                          isBold>
                          {displayValue}
                        </Text>
                      </KeyboardFocusable>
                    </View>
                  );
                })
            )}

            <KeyboardFocusable
              testID={ManageMySiteViewTestIDs.social.editAllButton}
              style={[
                styles.socialBtn,
                (showSocialLoader || isSocialMediaError) &&
                  styles.disabledSocialMediaButton,
              ]}
              onPress={onEditSocialLinks}
              disabled={showSocialLoader || isSocialMediaError}
              accessible
              focusable={!showSocialLoader}
              accessibilityRole="button"
              accessibilityState={{
                disabled: showSocialLoader || isSocialMediaError,
              }}
              accessibilityLabel={
                hasLinks ? 'Edit social links' : 'Add social links'
              }>
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                accessible={false}
                style={[
                  styles.socialBtnText,
                  showSocialLoader && styles.disabledSocialMediaButtonText,
                ]}>
                {hasLinks
                  ? tt('editSocialLinks', 'Edit social links')
                  : tt('addSocialLinks', 'Add social links')}
              </Text>
            </KeyboardFocusable>
          </>
        )}
      </ScrollView>
    </ModalHeader>
  );
};

export const ManageMySiteViewScreen = React.memo(
  ManageMySiteViewScreenComponent,
);
